import { useContext } from "react";
import { Button, Tag, TableEmpty, TileGrid } from "@/shared/rd";
import { useLocale, useTranslations } from "@/lib/i18n/intl";
import { keys } from "@/lib/i18n/portal";
import type { WhatsappAccount } from "../types/whatsapp.types";
import { useWhatsappQuery } from "../services/useWhatsapp";
import { Metric, QueryState } from "./Shared";
import { muted } from "./styles";
import { SetupCard } from "./SetupCard";
import { WhatsappFilterContext } from "./filter-context";

const copy = keys.whatsapp;
const BADGE = { sent: "send", delivered: "read" } as const;

export function OverviewPanel({
  query,
  account,
  onMessages,
  onSettings,
  onTemplates,
}: {
  query: string;
  account: WhatsappAccount;
  onMessages: () => void;
  onSettings: () => void;
  onTemplates: () => void;
}) {
  const t = useTranslations();
  const locale = useLocale();
  const analytics = useWhatsappQuery(
    "analytics",
    query,
    account.readiness.managementReady,
  );
  const data = analytics.data;
  const { widenPeriod } = useContext(WhatsappFilterContext);
  if (!account.readiness.managementReady)
    return <SetupCard readiness={account.readiness} onSettings={onSettings} />;
  // Meta reported zero for both: say so instead of two zero tiles.
  const nothingSent = data?.sent === 0 && data.delivered === 0;
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 16, minWidth: 0 }}>
      <div style={{ display: "flex", flexWrap: "wrap", alignItems: "center", justifyContent: "space-between", gap: 12 }}>
        <Tag tone="grey">{t(copy.metaSource)}</Tag>
        <div style={{ display: "flex", flexWrap: "wrap", gap: 8 }}>
          <Button variant="secondary" size="sm" onClick={onTemplates}>
            {t(copy.viewTemplates)}
          </Button>
          <Button variant="secondary" size="sm" onClick={() => onMessages()}>
            {t(copy.viewAllMessages)}
          </Button>
        </div>
      </div>
      <QueryState
        loading={analytics.isLoading}
        error={analytics.error}
        retry={analytics.refetch}
        page={t(copy.overview).toLocaleLowerCase(locale)}
      >
        {data && nothingSent ? (
          <section className="rd-card">
            <TableEmpty
              illustration="admin-whatsapp-empty"
              title={t("whatsapp.overviewEmptyTitle")}
              actions={
                widenPeriod ? (
                  <Button variant="secondary" size="sm" onClick={widenPeriod}>
                    {t("whatsapp.showLast90Days")}
                  </Button>
                ) : undefined
              }
            >
              {t("whatsapp.overviewEmptyText")}
            </TableEmpty>
          </section>
        ) : null}
        {data && !nothingSent && (
          <TileGrid min={250}>
            {(["sent", "delivered"] as const).map((field) => (
              <Metric
                key={field}
                badge={BADGE[field]}
                label={t(copy[field])}
                value={
                  data[field] === null
                    ? t(copy.notAvailable)
                    : data[field].toLocaleString(locale)
                }
              />
            ))}
          </TileGrid>
        )}
      </QueryState>
      <p style={muted}>{t(copy.metaAnalyticsHint)}</p>
    </div>
  );
}
